import { useEffect, useMemo, useState } from 'react';
import type {
  FetchRecommendationsDataArgs,
  IProductBasic,
  RecommendationsServingConfig,
} from '@noths/polaris-client-listings-api';
import {
  fetchRecommendationsData,
  ListingsRequestAndFilterBuilder,
  ListingsRequestTextFilterBuilder,
  RecommendationsRequestBuilder,
} from '@noths/polaris-client-listings-api';
import { generateRandomString, isBrowser } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';
import { parseCookies } from 'nookies';

import { isProd } from 'src/environment';
import type { TrackingProductData } from 'src/tracking/common/products';
import type { ProductCarouselProduct } from 'src/types/Recommendations';
import { logger } from 'src/utils/serverLogger';
import { transformRecommendationsServiceProductToTrackingProductData } from '../utils/transformRecommendationsServiceProductToTrackingProductData';

const createInStockFilter = () => {
  const inStockFilter = new ListingsRequestTextFilterBuilder('availability', ['IN_STOCK']).build();
  const filterCollection = [inStockFilter];
  return new ListingsRequestAndFilterBuilder().filters(filterCollection).build();
};

const transformRecommendationsProductToCarouselProduct = (
  product: Omit<IProductBasic, 'isExpandedSearch'>,
  currencyCode: CurrencyCode,
): ProductCarouselProduct => {
  const price = product.prices.find((p) => p.currency === currencyCode);
  const preSalePrice = product.preSalePrices?.find((p) => p.currency === currencyCode);

  return {
    title: product.title,
    src: product.images[0]?.href || '',
    href: product.links[0]?.rel || '',
    alt: '',
    isNew: product.isNew,
    onSale: product.onSale,
    isPurchasable: product.purchasable,
    price: price ? price.amount : 0,
    preSalePrice: preSalePrice ? preSalePrice.amount : undefined,
    salePercentage: product.salePercentage ?? undefined,
  };
};

export interface UseListingsRecommendationsProps {
  currency: CurrencyCode;
  servingConfig: RecommendationsServingConfig;
}

type CreateRecommendationsParametersProps = Pick<
  UseListingsRecommendationsProps,
  'servingConfig'
> & {
  currencyCode: CurrencyCode;
  userId?: string;
  visitorId: string;
};

const createRecommendationsParameters = ({
  currencyCode,
  servingConfig,
  userId,
  visitorId,
}: CreateRecommendationsParametersProps): FetchRecommendationsDataArgs => {
  const requestBuilder = new RecommendationsRequestBuilder();
  requestBuilder.currency(currencyCode);
  requestBuilder.eventType('home-page-view');
  requestBuilder.pageSize(10);
  const filter = createInStockFilter();
  requestBuilder.filter(filter);
  const body = requestBuilder.build();

  const clientSide = isBrowser();
  return {
    body,
    clientSide,
    env: isProd() ? 'prod' : 'qa',
    servingConfig,
    timeout: clientSide ? 15000 : 5500,
    userId,
    visitorId,
  };
};

export const useListingsRecommendations = ({
  currency,
  servingConfig,
}: UseListingsRecommendationsProps) => {
  const [recommendedProducts, setRecommendedProducts] = useState<ProductCarouselProduct[] | null>(
    null,
  );

  const [productsToTrack, setProductsToTrack] = useState<TrackingProductData[]>([]);

  const { _ga: googleAnalyticsVisitorId, cognitoId } = parseCookies();

  const visitorId = useMemo(() => {
    return googleAnalyticsVisitorId ?? generateRandomString(10);
  }, [googleAnalyticsVisitorId]);

  const currencyCode = useMemo(() => {
    return currency as CurrencyCode;
  }, [currency]);

  const userId = useMemo(() => {
    return cognitoId;
  }, [cognitoId]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setRecommendedProducts(null);
        const fetchParams = createRecommendationsParameters({
          currencyCode,
          servingConfig,
          userId: userId ?? undefined,
          visitorId,
        });

        const responseData = await fetchRecommendationsData(fetchParams);

        if ('message' in responseData) {
          setRecommendedProducts([]);
          logger.error('Error getting recommendations from listings-api:', responseData.message);
          return;
        }

        const adaptedData = responseData.products.map((product) =>
          transformRecommendationsProductToCarouselProduct(product, currencyCode),
        );
        setRecommendedProducts(adaptedData);
        const adapterProductsToTrack = responseData.products.map((p) =>
          transformRecommendationsServiceProductToTrackingProductData(p),
        );
        setProductsToTrack(adapterProductsToTrack);
      } catch (error) {
        setRecommendedProducts([]);
        logger.error('Exception thrown while getting recommendations from listings-api:', error);
      }
    };

    fetchProducts();
  }, [currencyCode, servingConfig, userId, visitorId]);

  return {
    recommendedProducts,
    productsToTrack,
    currencyCode,
  };
};
